/*
  Tuff Pupper: cross-axis variant availability filter.

  Problem this solves:
    The native Ignite/Tails picker greys out an option value only when no
    variant with that single value is available. It does NOT cross-filter
    across the other selected options. So on the Tracker harness the
    customer can pick Size = X-Large then see all five Color swatches as
    available, even though Hot Pink/X-Large and Purple/X-Large do not exist.
    Clicking one of those silently jumps to a different size.

  How it works:
    Reads every variant from <script data-tuff-variants> emitted by
    product-variant-picker.liquid. On every option change (via the existing
    PUB_SUB_EVENTS.optionValueSelectionChange bus published by
    <variant-selects>) it recomputes which option values are still reachable
    given the OTHER currently-selected options, then toggles `disabled`
    class on the inputs.

  Behaviour:
    - Adds `disabled` class to the input (matches the existing CSS pattern
      used by component-product-variant-picker.css and component-swatch-input.css
      for visually-disabled state).
    - Marks the input element disabled only when the combination does not
      exist, so a sold-out one can still be picked and shows Sold out.
    - The currently-selected value is never disabled, even if it later
      becomes impossible, so the picker stays reversible.
    - Skips entirely on products with no Color option or only one option.

  No globals are overridden, no MutationObserver loops, no !important in CSS.
*/

(function () {
  function init(picker) {
    if (!picker || picker.__tuffCrossFilterReady) return;
    picker.__tuffCrossFilterReady = true;

    const variantsNode = picker.querySelector('script[data-tuff-variants]');
    if (!variantsNode) return;

    let variants;
    try {
      variants = JSON.parse(variantsNode.textContent || '[]');
    } catch (e) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    // Build the input index: { 1: [inputEl, ...], 2: [inputEl, ...], ... }
    // keyed by option position. We support `select` plus `input[type=radio]`.
    function indexInputs() {
      const byPosition = {};
      picker.querySelectorAll('[data-option-position][data-option-value]').forEach((el) => {
        const position = parseInt(el.dataset.optionPosition, 10);
        if (!position) return;
        if (!byPosition[position]) byPosition[position] = [];
        byPosition[position].push(el);
      });
      return byPosition;
    }

    function getCurrentSelection(byPosition) {
      // For each option, return the currently-selected value, or null.
      const out = {};
      Object.keys(byPosition).forEach((pos) => {
        const inputs = byPosition[pos];
        const selected = inputs.find((el) => {
          if (el.tagName === 'OPTION') return el.selected;
          return el.checked === true;
        });
        out[pos] = selected ? selected.dataset.optionValue : null;
      });
      return out;
    }

    function variantMatches(variant, selection, ignorePosition) {
      // Variant must match every selected option except the one being tested.
      for (let i = 1; i <= 3; i++) {
        if (i === ignorePosition) continue;
        const selected = selection[i];
        if (!selected) continue;
        const variantValue = variant['option' + i];
        if (variantValue !== selected) return false;
      }
      return true;
    }

    function recompute() {
      const byPosition = indexInputs();
      const selection = getCurrentSelection(byPosition);

      Object.keys(byPosition).forEach((posKey) => {
        const position = parseInt(posKey, 10);
        const inputs = byPosition[position];

        inputs.forEach((input) => {
          const value = input.dataset.optionValue;
          // Variants matching the other selected options with this value here.
          const matching = variants.filter(
            (v) => variantMatches(v, selection, position) && v['option' + position] === value
          );
          const exists = matching.length > 0;
          const isAvailable = matching.some((v) => v.available);

          // The currently-checked value stays interactive so the user can
          // change away from it. Only non-selected impossible values are
          // disabled.
          const isCurrentlySelected =
            (input.tagName === 'OPTION' && input.selected) ||
            input.checked === true;

          // A sold-out combination stays tappable so the button can say Sold out;
          // only a combination that does not exist is locked.
          const shouldDisable = !exists && !isCurrentlySelected;

          input.classList.toggle('disabled', !isAvailable);
          // Use the native disabled attribute so a click on the input or
          // its label cannot toggle it. Skip disabling SELECT options to
          // avoid surprising the dropdown picker shape; on dropdowns we
          // rely on the `.disabled` class for styling.
          if (input.tagName === 'INPUT') {
            if (shouldDisable) {
              input.setAttribute('disabled', 'disabled');
              input.setAttribute('aria-disabled', 'true');
            } else {
              input.removeAttribute('disabled');
              input.removeAttribute('aria-disabled');
            }
          }
        });
      });
    }

    // Run once on mount so the page-load state is correct.
    recompute();

    // Subscribe to the same bus the rest of the picker uses. The event
    // fires AFTER the radio/select state changes, so by the time we read
    // the selection it already reflects the new pick.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (payload) => {
        const target = payload && payload.data && payload.data.event && payload.data.event.target;
        if (!target || !picker.contains(target)) return;
        // Defer one frame so any concurrent state mutation (e.g. label
        // refresh, server fetch starting) has settled.
        requestAnimationFrame(recompute);
      });
    }

    // Also re-run after product-info finishes swapping the variant-selects
    // markup. product-info.js fires variantChange and may replace the
    // <variant-selects> innerHTML, so our index needs rebuilding.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        // After view transition, find the same picker again; if it was
        // replaced, the new instance will pick this up on its own connect.
        if (!document.contains(picker)) return;
        requestAnimationFrame(recompute);
      });
    }
  }

  function boot() {
    document.querySelectorAll('variant-selects').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run when product-info swaps variant-selects HTML. Run a single light
  // pass on every section:load + variantChange instead of any global hook.
  document.addEventListener('shopify:section:load', boot);

  // The theme's view-transition update path replaces the <product-info>
  // element wholesale when the variant changes, so the freshly-rendered
  // <variant-selects> needs init() again. Listen on the bare global
  // subscribe so we re-boot every time a variant change finishes rendering.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      // Give the view transition a frame to commit the new DOM, then init
      // any picker that has not been bound yet.
      requestAnimationFrame(() => requestAnimationFrame(boot));
    });
  } else {
    // Fall back to polling for the globals (they are declared after this
    // script in the head, but defer guarantees order; this is a belt+braces).
    document.addEventListener('DOMContentLoaded', () => {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => {
          requestAnimationFrame(() => requestAnimationFrame(boot));
        });
      }
    });
  }
})();
